
import { ParamMap } from '@angular/router';

import { isNullOrUndefined } from '@app/util/util';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { STEP_SECTION } from '@packages/packages-result/constants/packages-result.routes';
import { PackageSeachResponse } from '@packages/packages-result/entities/packages-search';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { environment as ENV } from '@environment';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { formatPackageSearchDate, reverseMapFlightClass } from '@packages/packages-search-bar/mappers/packages-url.mapper';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { BRANCH_CODE, SOURCE_PARAM } from '@app/modules/core/constants/query-params';

export const PACKAGES_RESULT_URL_PARAMS_POSITION = {
  FLIGHT_CLASS: 2,
  DEPARTURE: 3,
  DESTINATION: 4,
  DEPARTURE_DATE: 5,
  ARRIVAL_DATE: 6,
  ROOMS: 7,
  BAGGAGE_INCLUDED: 8,
  AIRLINE: 9,
  STEP: 10
};

export const PACKAGES_HOTEL_DETAIL_URL_PARAMS_POSITION = {
  HOTEL_ID: 2,
  DEPARTURE: 3,
  ARRIVAL: 4,
  DEPARTURE_DATE: 5,
  ARRIVAL_DATE: 6,
  ROOMS: 7,
  CURRENCY: 8,
  STEP: 9
};

export function encodeURLParams(str: string): string {
  return !isNullOrUndefined(str) ? btoa(unescape(encodeURI(str))) : null;
}

export function decodeURLParams(str: string): string {
  return !isNullOrUndefined(str) ? decodeURIComponent(escape(atob(str))) : null;
}

export function getFlightClassByResultURL(
  decodeUrl: string, position: number = PACKAGES_RESULT_URL_PARAMS_POSITION.FLIGHT_CLASS): FlightClass {
  const splitBaseUrl = decodeUrl ? decodeUrl.split('/') : null;
  return splitBaseUrl && splitBaseUrl.length && splitBaseUrl[position] ? reverseMapFlightClass(splitBaseUrl[position]) :
    FlightClass.CHEAPER;
}

export function getParamByPackResultURL(decodeUrl: string, position: number): string {
  const splitBaseUrl = decodeUrl ? decodeUrl.split('/') : null;
  return splitBaseUrl && splitBaseUrl.length && splitBaseUrl[position] ? splitBaseUrl[position] : null;
}

export function hotelResultsToHotelDetailRouteMapper(
  hotelDetailRootRoute: string, search: PackageSeachResponse, hotel: HotelSearched,
  queries: ParamMap, rooms: string): string {
  return `/${hotelDetailRootRoute}/${search.sessionToken}/${search.SearchId}` +
    `/${hotel.OptionId}/${search.SearchId}/${search.SelectedAirOptionId}/${rooms}/${PackageStep.AIR_PACK_HOTEL_DETAIL}` +
    `?${SOURCE_PARAM}=${queries.get(SOURCE_PARAM)}&${BRANCH_CODE}=${queries.get(BRANCH_CODE)}`;
}

export function hotelDetailEditMapper(hotelDetailRootRoute: string, hotelDetail: HotelDetailResponse, queries: ParamMap,
  packageResultsUrl: string, rooms: string, searchData: SearchData): string {
  const departure = getParamByPackResultURL(packageResultsUrl, PACKAGES_RESULT_URL_PARAMS_POSITION.DEPARTURE);
  return `/${hotelDetailRootRoute}/${hotelDetail.Result.HotelCode}/${departure}/${hotelDetail.Result.Destination}` +
    `/${formatPackageSearchDate(searchData.departureDate)}/${formatPackageSearchDate(searchData.arrivalDate)}/${rooms}` +
    `/${ENV.countryInfo.currency_code}/${PackageStep.AIR_PACK_HOTEL_DETAIL}?${SOURCE_PARAM}=${queries.get(SOURCE_PARAM)}` +
    `&${BRANCH_CODE}=${queries.get(BRANCH_CODE)}`;
}

export function hotelResultsToPackageFlightRouteMapper(decodeUrl: string, queries: ParamMap): string {
  return `${decodeUrl.replace(STEP_SECTION, PackageStep.FLIGHT_RESULTS)}` +
    `?${SOURCE_PARAM}=${queries.get(SOURCE_PARAM)}&${BRANCH_CODE}=${queries.get(BRANCH_CODE)}`;
}

export function packageFlightsToPackageHotelsRouteMapper(currentUrl: string, queries: ParamMap): string {
  return `${currentUrl.replace(PackageStep.FLIGHT_RESULTS, PackageStep.HOTEL_RESULTS)}` +
    `?${SOURCE_PARAM}=${queries.get(SOURCE_PARAM)}&${BRANCH_CODE}=${queries.get(BRANCH_CODE)}`;
}

export function packageHotelsToPackageFlightsRouteMapper(currentUrl: string, queries: ParamMap): string {
  return `${currentUrl.replace(PackageStep.HOTEL_RESULTS, PackageStep.FLIGHT_RESULTS)}` +
    `?${SOURCE_PARAM}=${queries.get(SOURCE_PARAM)}&${BRANCH_CODE}=${queries.get(BRANCH_CODE)}`;
}

export function redirectFlightToHotelMapper(currentUrl: string, queries: ParamMap): string {
  return `${currentUrl.replace(PackageStep.FLIGHT_RESULTS, PackageStep.HOTEL_RESULTS)}` +
    `?${SOURCE_PARAM}=${queries.get(SOURCE_PARAM)}&${BRANCH_CODE}=${queries.get(BRANCH_CODE)}`;
}

export function setBasePackageResultURL(decodeUrl: string, detailSearchUrl: string): string {
  const newDepartureDate = getParamByPackageHotelDetailURL(detailSearchUrl,
    PACKAGES_HOTEL_DETAIL_URL_PARAMS_POSITION.DEPARTURE_DATE);
  const newArrivalDate = getParamByPackageHotelDetailURL(detailSearchUrl,
    PACKAGES_HOTEL_DETAIL_URL_PARAMS_POSITION.ARRIVAL_DATE);
  const newGuestFormat = getParamByPackageHotelDetailURL(detailSearchUrl,
    PACKAGES_HOTEL_DETAIL_URL_PARAMS_POSITION.ROOMS);
  const splitBaseUrl = decodeUrl ? decodeUrl.split('/') : null;
  if (splitBaseUrl && splitBaseUrl.length &&
    splitBaseUrl[PACKAGES_RESULT_URL_PARAMS_POSITION.DEPARTURE_DATE] &&
    splitBaseUrl[PACKAGES_RESULT_URL_PARAMS_POSITION.ARRIVAL_DATE] &&
    splitBaseUrl[PACKAGES_RESULT_URL_PARAMS_POSITION.ROOMS]) {
    splitBaseUrl[PACKAGES_RESULT_URL_PARAMS_POSITION.DEPARTURE_DATE] = newDepartureDate;
    splitBaseUrl[PACKAGES_RESULT_URL_PARAMS_POSITION.ARRIVAL_DATE] = newArrivalDate;
    splitBaseUrl[PACKAGES_RESULT_URL_PARAMS_POSITION.ROOMS] = newGuestFormat;
    return splitBaseUrl.join('/');
  } else return null;
}

function getParamByPackageHotelDetailURL(decodeUrl: string, position: number): string {
  const splitBaseUrl = decodeUrl ? decodeUrl.split('/') : null;
  return splitBaseUrl && splitBaseUrl.length && splitBaseUrl[position] ? splitBaseUrl[position] : null;
}
